/**
 * RecordRuns: a failure record's runs as a line of dots each, one dot per
 * assistant turn, and the turn a click picks.
 *
 * A diamond is a call to a tool the record names as wrong, its run's first
 * one outlined; a filled dot is a call to the tool the record expects; a
 * small dot another call; a hollow dot a reply with no call. The api says
 * which (`RecordTurn.kind`). The legend marks each call the way its dots
 * are marked, and says a reply where the analyst's word stands in for a
 * tool. Failed runs come first. The panel under the rows shows the picked
 * turn's text, calls and the tool results that answered them, and opens
 * the run's whole transcript on request.
 */

import { useState } from 'react'

import { getApiErrorDetail } from '@/api/client'
import type {
  FailureRecordResponse,
  RecordRun,
  RecordTurn,
  RecordTurnKind,
} from '@/api/generated/model'
import { Badge } from '@/components/ui/badge'
import { EYEBROW } from '@/components/ui/eyebrow'
import { useFailureRecordTranscript } from '@/hooks/useFailureRecord'
import { cn } from '@/lib/utils'

import {
  RECORD_COPY,
  TURN_KIND_LABEL,
  argumentUntold,
  failedRunsReadSentence,
  firstSelection,
  legendSentences,
  madeCallKind,
  namesNoCall,
  pivotOf,
  turnKey,
  turnPlace,
} from './record'

const MARK: Record<RecordTurnKind, string> = {
  wrong: 'h-2.5 w-2.5 rotate-45 rounded-[1px] bg-destructive',
  expected: 'h-3 w-3 rounded-full bg-success',
  tool: 'h-2 w-2 rounded-full bg-muted-foreground',
  text: 'h-2.5 w-2.5 rounded-full border-[1.5px] border-muted-foreground bg-background',
}
const PIVOT = 'ring-2 ring-destructive ring-offset-2 ring-offset-background'
const KIND_BADGE: Record<RecordTurnKind, 'destructive' | 'success' | 'outline' | 'secondary'> = {
  wrong: 'destructive',
  expected: 'success',
  tool: 'outline',
  text: 'secondary',
}
/** One dot's box: the click target and its place on the axis. */
const DOT = 'h-9 w-9 shrink-0'
const LABEL_COLUMN = 'w-48 shrink-0'
const PRE = 'whitespace-pre-wrap break-words rounded-sm bg-muted/40 px-3 py-2 font-mono text-xs'

function Mark({ kind, pivot }: { kind: RecordTurnKind; pivot: boolean }): React.JSX.Element {
  return <span aria-hidden="true" className={cn('block', MARK[kind], pivot && PIVOT)} />
}

/** A tool by its name, or the reply the analyst's word stands in for. */
function CallName({ record, tool }: { record: FailureRecordResponse; tool: string }): React.JSX.Element {
  return namesNoCall(record, tool) ? (
    <span>{RECORD_COPY.textReply}</span>
  ) : (
    <span className="font-mono text-xs">{tool}</span>
  )
}

function Legend({ record }: { record: FailureRecordResponse }): React.JSX.Element {
  const failedRunsRead = failedRunsReadSentence(record)
  const rules = legendSentences(record)
  const expected = record.call_expected.tool
  return (
    <div className="space-y-2 border-y border-border py-3 text-sm">
      <ul className="grid gap-x-8 gap-y-2 md:grid-cols-2">
        {record.calls_made.map((call) => (
          <li key={call.tool} className="flex items-baseline gap-2.5">
            <span className="flex h-4 w-3 items-center justify-center">
              <Mark kind={madeCallKind(record, call.tool)} pivot={false} />
            </span>
            <span>
              <CallName record={record} tool={call.tool} />{' '}
              <span className={EYEBROW}>{call.runs === 1 ? '1 run' : `${call.runs.toString()} runs`}</span>{' '}
              <span className="text-muted-foreground">{call.note}</span>
            </span>
          </li>
        ))}
        <li className="flex items-baseline gap-2.5">
          <span className="flex h-4 w-3 items-center justify-center">
            <Mark kind={namesNoCall(record, expected) ? 'text' : 'expected'} pivot={false} />
          </span>
          <span>
            <CallName record={record} tool={expected} />{' '}
            <span className={EYEBROW}>{RECORD_COPY.expected}</span>{' '}
            <span className="text-muted-foreground">{record.call_expected.args}</span>
          </span>
        </li>
        <li className="flex items-center gap-4 text-muted-foreground">
          <span className="flex items-center gap-2">
            <Mark kind="tool" pivot={false} />
            {RECORD_COPY.otherTool}
          </span>
          <span className="flex items-center gap-2">
            <Mark kind="text" pivot={false} />
            {RECORD_COPY.textReply}
          </span>
        </li>
      </ul>
      {rules.length > 0 && <p className="text-xs text-muted-foreground">{rules.join(' ')}</p>}
      {argumentUntold(record) && (
        <p className="text-xs text-muted-foreground">{RECORD_COPY.argumentUntold}</p>
      )}
      {failedRunsRead !== null && <p className="text-xs text-muted-foreground">{failedRunsRead}</p>}
    </div>
  )
}

function RunRow({
  run,
  picked,
  onPick,
  first,
  pivotLabel,
}: {
  run: RecordRun
  picked: string | null
  onPick: (key: string) => void
  first: boolean
  pivotLabel: string
}): React.JSX.Element {
  const pivot = pivotOf(run)
  return (
    <div className={cn('flex items-center gap-4 border-t', first ? 'mt-1 border-border' : 'border-border/50')}>
      <div className={cn(LABEL_COLUMN, 'flex items-center gap-2')}>
        <span className="font-mono text-xs">
          {RECORD_COPY.run} {run.pass_index}
        </span>
        <Badge variant={run.passed ? 'success' : 'destructive'}>
          {run.passed ? RECORD_COPY.passed : RECORD_COPY.failed}
        </Badge>
        {run.unread && <Badge variant="outline">{RECORD_COPY.notRead}</Badge>}
      </div>
      <div className="relative flex items-center gap-1">
        {run.turns.length > 1 && (
          <span aria-hidden="true" className="absolute left-[18px] right-[18px] top-1/2 h-px bg-border" />
        )}
        {run.turns.map((turn) => {
          const key = turnKey(run, turn)
          const isPivot = turn === pivot
          return (
            <button
              key={key}
              type="button"
              aria-pressed={picked === key}
              aria-label={`${turnPlace(run, turn)}: ${TURN_KIND_LABEL[turn.kind]}${isPivot ? `, ${pivotLabel}` : ''}`}
              onClick={() => {
                onPick(key)
              }}
              className={cn(
                DOT,
                'relative z-10 flex items-center justify-center rounded-sm border border-transparent',
                picked === key ? 'border-foreground bg-muted' : 'hover:bg-muted/60',
              )}
            >
              <Mark kind={turn.kind} pivot={isPivot} />
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface TranscriptProps {
  recordId: string
  round: string
  name: string
}

function Transcript({ recordId, round, name }: TranscriptProps): React.JSX.Element {
  const { data, isLoading, isError, error } = useFailureRecordTranscript(recordId, round, name)
  if (isLoading) return <p className="text-xs text-muted-foreground">{RECORD_COPY.transcriptLoading}</p>
  if (isError || data === undefined) {
    return (
      <p className="text-xs text-destructive">
        {RECORD_COPY.transcriptFailed} {getApiErrorDetail(error) ?? name}
      </p>
    )
  }
  return (
    <div className="space-y-2">
      <pre className={cn(PRE, 'max-h-[32rem] overflow-y-auto')}>{data.text}</pre>
      {data.truncated && <p className="text-xs text-muted-foreground">{RECORD_COPY.transcriptTruncated}</p>}
    </div>
  )
}

function TurnPanel({
  recordId,
  round,
  run,
  turn,
}: {
  recordId: string
  round: string
  run: RecordRun
  turn: RecordTurn
}): React.JSX.Element {
  const [open, setOpen] = useState(false)
  return (
    <section aria-live="polite" className="space-y-3 rounded-sm border border-border px-4 py-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="font-mono text-xs">{turnPlace(run, turn)}</span>
        <Badge variant={KIND_BADGE[turn.kind]}>{TURN_KIND_LABEL[turn.kind]}</Badge>
      </div>
      {turn.text !== '' && <p className="whitespace-pre-wrap text-sm">{turn.text}</p>}
      {turn.calls.length > 0 && (
        <div className="space-y-1">
          <p className={EYEBROW}>{RECORD_COPY.calls}</p>
          <pre className={PRE}>{turn.calls.join('\n')}</pre>
        </div>
      )}
      {turn.calls.length > 0 && (
        <div className="space-y-1">
          <p className={EYEBROW}>{RECORD_COPY.result}</p>
          {turn.result === '' ? (
            <p className="text-xs text-muted-foreground">{RECORD_COPY.noResult}</p>
          ) : (
            <pre className={cn(PRE, 'max-h-64 overflow-y-auto text-muted-foreground')}>{turn.result}</pre>
          )}
        </div>
      )}
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current)
        }}
        className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        {open ? RECORD_COPY.closeTranscript : RECORD_COPY.openTranscript}
      </button>
      {open && <Transcript recordId={recordId} round={round} name={run.transcript} />}
    </section>
  )
}

export function RecordRuns({ record }: { record: FailureRecordResponse }): React.JSX.Element {
  const [picked, setPicked] = useState<string | null>(() => firstSelection(record.runs))
  const longest = Math.max(0, ...record.runs.map((run) => run.turns.length))
  const firstPassed = record.runs.findIndex((run) => run.passed)
  const chosen = record.runs.flatMap((run) =>
    run.turns.filter((turn) => turnKey(run, turn) === picked).map((turn) => ({ run, turn })),
  )[0]
  if (record.runs.length === 0) {
    return <p className="text-sm text-muted-foreground">{RECORD_COPY.noRuns}</p>
  }
  return (
    <div className="space-y-4">
      <Legend record={record} />
      <div role="group" aria-label={RECORD_COPY.turnsLabel} className="overflow-x-auto">
        <div className="min-w-max">
          <div aria-hidden="true" className="flex items-center gap-4 pb-1">
            <span className={cn(LABEL_COLUMN, EYEBROW)}>{RECORD_COPY.turnAxis}</span>
            <div className="flex gap-1">
              {Array.from({ length: longest }, (_, i) => (
                <span key={i} className={cn(DOT, 'flex h-auto items-center justify-center font-mono text-[10px] text-muted-foreground')}>
                  {i + 1}
                </span>
              ))}
            </div>
          </div>
          {record.runs.map((run, index) => (
            <RunRow
              key={run.transcript}
              run={run}
              picked={picked}
              onPick={setPicked}
              first={index === firstPassed && index > 0}
              pivotLabel={record.failing_turns_named ? RECORD_COPY.failingTurn : RECORD_COPY.firstWrong}
            />
          ))}
        </div>
      </div>
      {chosen !== undefined && (
        <TurnPanel
          key={turnKey(chosen.run, chosen.turn)}
          recordId={record.id}
          round={record.round}
          run={chosen.run}
          turn={chosen.turn}
        />
      )}
    </div>
  )
}
